import React from 'react';
import { Stepper } from '@printdeal/ui-library';
import { FormattedMessage } from 'react-intl';
import { useFeatureToggle } from '@printdeal/feature-management/react';
import { progressBarTranslations as levels } from '../../helpers/segmentedCustomerHelper';

interface CustomerCategoryProp {
  [key: string]: number;
}

interface ProgressProp {
  [key: string]: string;
}

interface ProgressBarProps {
  revenue365: number;
  customersCategory: string;
}

const customerCategoriesNew: CustomerCategoryProp = {
  basic: 0,
  plus: 1000,
  pro: 2500,
  premium: 5000,
  excellent: 25000,
};

const customerCategoriesOld: CustomerCategoryProp = {
  basic: 0,
  plus: 1000,
  premium: 30000,
};

const ProgressBar = ({ revenue365, customersCategory }:ProgressBarProps) => {
  const { featureToggle: { isEnabled: isNewProgressBar } } = useFeatureToggle('new_progress_bar');
  const customerCategories = isNewProgressBar ? customerCategoriesNew : customerCategoriesOld;
  const categories: string[] = Object.keys(customerCategories);
  const progress: ProgressProp = {};

  categories.forEach((category, index) => {
    if (index !== categories.length - 1) {
      if (revenue365 <= customerCategories[categories[index]]) {
        progress[category] = '0%';
      } else if (revenue365 > customerCategories[categories[index + 1]]) {
        progress[category] = '100%';
      } else {
        const progressLength = (customerCategories[categories[index + 1]] - customerCategories[categories[index]]);
        const progressPerc = (
          (revenue365 - customerCategories[categories[index]]) / progressLength
        ) * 100;
        progress[category] = `${progressPerc}%`;
      }
    }
  });

  const getStatusColor = (category: string) => (customersCategory.toLowerCase() === category ? 'success' : 'default');

  const getCategoryValues = (index: number) => {
    const categoryValues = (
      <p className={`pd-font-semibold pd-text-dim-500 ${index === categories.length - 1 ? 'pd-ml-[-0.5rem]' : ''}`}>
        <FormattedMessage
          id="account.dashboard.customerProgram.category.value.pretext"
          defaultMessage="From"
        /> &euro; {customerCategories[categories[index]]?.toLocaleString().replace(/,/g, '.')}
      </p>
    );

    return categoryValues;
  };

  const getWeight = (index: number) => {
    if (isNewProgressBar) {
      return index === 3 ? 3 : 1;
    }
    return index === 1 ? 3 : 1;
  };

  return (
    <Stepper className="pd-my-5">
      <>
        {categories.map((category, index) => (
          <React.Fragment key={category}>
            <Stepper.Node variant={getStatusColor(category)}>
              <>
                <FormattedMessage
                  id={levels[category].id}
                  defaultMessage={levels[category].defaultMessage}
                />
                {getCategoryValues(index)}
              </>
            </Stepper.Node>
            {index < categories.length - 1 && (
            <Stepper.Edge
              variant="primary"
              progress={parseFloat(progress[category]) / 100}
              weight={getWeight(index)}
            />
            )}
          </React.Fragment>
        ))}
      </>
    </Stepper>
  );
};

export default ProgressBar;
